import React, { useState } from 'react';
import { Phone, X, Delete } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PhoneDialerProps {
  onCall: (phoneNumber: string) => void;
  initialNumber?: string;
}

export const PhoneDialer: React.FC<PhoneDialerProps> = ({
  onCall,
  initialNumber = ''
}) => {
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);

  const handleDigitClick = (digit: string) => {
    if (phoneNumber.length < 15) {
      setPhoneNumber(phoneNumber + digit);
    }
  };

  const handleDelete = () => {
    setPhoneNumber(phoneNumber.slice(0, -1));
  };

  const handleClear = () => {
    setPhoneNumber('');
  };

  const handleCall = () => {
    if (phoneNumber.length >= 10) {
      onCall(phoneNumber);
    }
  };

  const formatDisplay = (number: string) => {
    // Format as user types: (415) 555-1234
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const dialPad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#']
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm mx-auto">
      {/* Display */}
      <div className="mb-6">
        <div className="bg-gray-50 rounded-lg p-4 min-h-[60px] flex items-center justify-between">
          <input
            type="tel"
            value={formatDisplay(phoneNumber)}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter phone number"
            className="text-2xl font-semibold bg-transparent outline-none w-full"
            maxLength={14}
          />
          {phoneNumber && (
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Dial Pad */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {dialPad.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {row.map((digit) => (
              <button
                key={digit}
                onClick={() => handleDigitClick(digit)}
                className="
                  aspect-square rounded-full bg-gray-100 hover:bg-gray-200
                  active:bg-gray-300 transition-colors
                  text-2xl font-semibold text-gray-800
                  flex items-center justify-center
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                "
              >
                {digit}
              </button>
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleDelete}
          disabled={!phoneNumber}
          className="
            flex-1 py-3 rounded-lg bg-gray-100 hover:bg-gray-200
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors flex items-center justify-center gap-2
          "
        >
          <Delete className="w-5 h-5" />
          Delete
        </button>

        <button
          onClick={handleCall}
          disabled={phoneNumber.replace(/\D/g, '').length < 10}
          className="
            flex-1 py-3 rounded-lg bg-v2accent hover:bg-v2accent/90
            disabled:opacity-50 disabled:cursor-not-allowed
            text-white font-semibold transition-colors
            flex items-center justify-center gap-2
          "
        >
          <Phone className="w-5 h-5" />
          Call
        </button>
      </div>

      {phoneNumber.replace(/\D/g, '').length < 10 && phoneNumber.length > 0 && (
        <p className="text-sm text-gray-500 text-center mt-3">
          Enter at least 10 digits to call
        </p>
      )}
    </div>
  );
};
